import React, { createContext, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type { NotificationChangeEvent } from '@teo/backend';
import { useSubscription } from '@trpc/tanstack-react-query';

import { trpc } from '../lib/trpc';
import { updateNotificationCaches } from '../utils/notificationCache';
import { useAuth } from './AuthContext';

export interface NotificationContextType {
  isConnected: boolean;
  lastEvent: NotificationChangeEvent | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

// eslint-disable-next-line react-refresh/only-export-components
export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [lastEvent, setLastEvent] = useState<NotificationChangeEvent | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'error'
  >('disconnected');

  // Single shared subscription for all notification features
  useSubscription(
    trpc.notifications.onNotificationChange.subscriptionOptions(undefined, {
      enabled: !authLoading && !!user,
      onStarted: () => {
        setConnectionStatus('connecting');
      },
      onData: (event: NotificationChangeEvent) => {
        setLastEvent(event);
        setConnectionStatus('connected');
        // Update all notification caches when we receive an event
        updateNotificationCaches(queryClient, event);
      },
      onError: (error) => {
        console.error('Notification subscription error:', error);
        setConnectionStatus('error');
      },
    }),
  );

  // Update connection status based on auth state
  useEffect(() => {
    if (authLoading) {
      setConnectionStatus('disconnected');
    } else if (!user) {
      setConnectionStatus('disconnected');
      setLastEvent(null);
    }
  }, [authLoading, user]);

  const contextValue: NotificationContextType = {
    isConnected: connectionStatus === 'connected',
    lastEvent,
    connectionStatus,
  };

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  );
}
